import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Operation, OperationImage, Side } from '../../lib/types';
import { uid } from '../../lib/id';
import { Button } from '../ui/Button';
import { Field, Input, Textarea } from '../ui/Field';
import { ImageUploader } from './ImageUploader';
import { JournalTagFields } from '../journal/JournalTagFields';
import { useJournal } from '../../context/JournalContext';
import { cn } from '../../lib/cn';
interface Props {
  date: string;
  initial?: Operation | null;
  onCancel: () => void;
  onSubmit: (op: Operation) => void;
}
export function OperationForm({
  date,
  initial,
  onCancel,
  onSubmit
}: Props) {
  const [operationDate, setOperationDate] = useState(initial?.date ?? date);
  const [entryTime, setEntryTime] = useState(initial?.entryTime ?? '');
  const [exitTime, setExitTime] = useState(initial?.exitTime ?? '');
  const [asset, setAsset] = useState(initial?.asset ?? '');
  const [side, setSide] = useState<Side>(initial?.side ?? 'buy');
  const [resultPct, setResultPct] = useState(initial ? String(initial.resultPct) : '');
  const [resultMoney, setResultMoney] = useState(initial?.resultMoney === undefined ? '' : String(initial.resultMoney));
  const [resultR, setResultR] = useState(initial ? String(initial.resultR) : '');
  const [followedPlan, setFollowedPlan] = useState<boolean | null>(initial?.followedPlan ?? null);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [images, setImages] = useState<OperationImage[]>(initial?.images ?? []);
  const [tagIds, setTagIds] = useState<string[]>(initial?.tagIds ?? []);
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const {
    categoriesSorted,
    state,
    tagsByCategory
  } = useJournal();
  // Every category with active tags is a required confirmation, so the metrics
  // work with whatever categories the user has created.
  const requiredCategories = categoriesSorted.filter(category => tagsByCategory(category.id).length > 0);
  const missing: string[] = [];
  requiredCategories.forEach(category => {
    const hasTag = state.tags.some(tag => tag.categoryId === category.id && tagIds.includes(tag.id));
    if (!hasTag) missing.push(category.name);
  });
  if (!resultPct.trim()) missing.push('Resultado %');
  if (followedPlan === null) missing.push('¿Se siguió el plan?');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (missing.length) {
      setShowErrors(true);
      return;
    }
    onSubmit({
      id: initial?.id ?? uid('op'),
      date: operationDate,
      entryTime,
      exitTime,
      asset: asset.trim() || '—',
      side,
      resultPct: parseFloat(resultPct) || 0,
      resultMoney: resultMoney.trim() ? parseFloat(resultMoney) || 0 : undefined,
      resultR: parseFloat(resultR) || 0,
      followedPlan,
      notes: notes.trim(),
      images,
      tagIds,
      createdAt: initial?.createdAt ?? Date.now()
    });
  };
  return <AnimatePresence data-id="element-463" data-mp="hyvmmaxjehle">
      <motion.div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-464" data-mp="hy2mvtwzwscr">
        <div className="absolute inset-0 bg-ink/25 backdrop-blur-[2px]" onClick={onCancel} data-id="element-465" data-mp="1anja8d5rdj24" />
        <motion.form onSubmit={submit} initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative z-10 flex max-h-[94vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-pop sm:rounded-2xl" data-id="element-466" data-mp="1al6vs9z7lqtt">
          <header className="flex items-center justify-between border-b border-line px-5 py-4" data-id="element-467" data-mp="3lgdv8kl4vsmv">
            <div data-id="element-468" data-mp="5i4kzavy5yhd"><h2 className="text-[15px] font-semibold tracking-tight" data-id="element-469" data-mp="5iw6fsvtyftx">{initial ? 'Editar operación' : 'Nueva operación'}</h2><p className="mt-0.5 text-[12px] text-ink-soft capitalize" data-id="element-470" data-mp="pbi6sk1h07x">{format(parseISO(operationDate), "EEEE d 'de' MMMM, yyyy", {
                locale: es
              })}</p></div>
            <button type="button" onClick={onCancel} className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-soft hover:bg-surface" aria-label="Cerrar" data-id="element-471" data-mp="3mym47oa81nw0"><XIcon className="h-4 w-4" data-id="element-472" data-mp="oe4dqnwr1620" /></button>
          </header>

          <div className="flex-1 overflow-y-auto px-5 py-5" data-id="element-473" data-mp="jifc8f165f32">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-474" data-mp="ehhwsa7jmz59">
              <Field label="Fecha" data-id="element-475" data-mp="ejuc0flx2vem"><Input type="date" value={operationDate} onChange={e => setOperationDate(e.target.value)} data-id="element-476" data-mp="9j6mjydc6mmi" /></Field>
              <Field label="Hora entrada" data-id="element-477" data-mp="2z8rw1k0ukni3"><Input type="time" value={entryTime} onChange={e => setEntryTime(e.target.value)} data-id="element-478" data-mp="2z91jp0yf6ikm" /></Field>
              <Field label="Hora salida" data-id="element-479" data-mp="2u7m0lefsym4v"><Input type="time" value={exitTime} onChange={e => setExitTime(e.target.value)} data-id="element-480" data-mp="2ubi868t3wjwd" /></Field>
              <Field label="Activo" data-id="element-481" data-mp="19pa53sd7ly9q"><Input placeholder="MNQ, ES, EURUSD…" value={asset} onChange={e => setAsset(e.target.value)} data-id="element-482" data-mp="1epobz732r266" /></Field>
              <Field label="Resultado % *" data-id="element-483" data-mp="1jo9e5c1vzvx9"><Input type="number" step="0.01" required placeholder="1.50" value={resultPct} onChange={e => setResultPct(e.target.value)} data-id="element-484" data-mp="1jnzm7ixa7eox" /></Field>
              <Field label="Resultado en R" data-id="element-485" data-mp="1tfn7t375q1ih"><Input type="number" step="0.01" placeholder="Opcional" value={resultR} onChange={e => setResultR(e.target.value)} data-id="element-486" data-mp="1tec3n29surqu" /></Field>
              <Field label="Resultado dinero" className="sm:col-span-1" data-id="element-487" data-mp="1xijv377n6lsr"><Input type="number" step="0.01" placeholder="Opcional" value={resultMoney} onChange={e => setResultMoney(e.target.value)} data-id="element-488" data-mp="22h3ilc1vw5n8" /></Field>
              <Field label="Compra / Venta" className="sm:col-span-2" data-id="element-489" data-mp="81r5tyj6cuib"><div className="grid h-10 grid-cols-2 gap-1.5 rounded-xl border border-line bg-surface p-1" data-id="element-490" data-mp="832iknullclk">{(['buy', 'sell'] as Side[]).map(value => <button key={value} type="button" onClick={() => setSide(value)} className={cn('rounded-lg text-[13px] font-medium transition-all', side === value ? value === 'buy' ? 'bg-white text-pos shadow-sm' : 'bg-white text-neg shadow-sm' : 'text-ink-soft')} data-id="element-491" data-mp="d2oy53xwf4xj">{value === 'buy' ? 'Compra' : 'Venta'}</button>)}</div></Field>
            </div>

            <div className="mt-5" data-id="element-492" data-mp="9yzj932lvbrp"><p className="mb-2 text-[13px] font-medium text-ink-soft" data-id="element-493" data-mp="9xo6idr6mtog">Confirmaciones de estrategia * <span className="font-normal" data-id="element-494" data-mp="nxz19rvm4sew">— obligatorias para el análisis de confluencias</span></p><JournalTagFields value={tagIds} onChange={setTagIds} data-id="element-495" data-mp="jy8bnrhtoiby" /></div>

            <div className="mt-5 rounded-xl border border-line bg-surface/40 p-3" data-id="element-496" data-mp="xok7o1hj318d"><p className="text-[12px] font-medium text-ink-soft" data-id="element-497" data-mp="xqxyfy56dra4">¿Se siguió el plan? *</p><div className="mt-2 flex gap-1.5" data-id="element-498" data-mp="sqaaxmg5nskn">{([{
                label: 'Sí',
                value: true
              }, {
                label: 'No',
                value: false
              }, {
                label: 'Sin indicar',
                value: null
              }] as const).map(option => <button key={option.label} type="button" onClick={() => setFollowedPlan(option.value)} className={cn('rounded-lg border px-3 py-1.5 text-[12px] font-medium', followedPlan === option.value ? option.value === true ? 'border-pos-border bg-pos-soft text-pos' : option.value === false ? 'border-neg-border bg-neg-soft text-neg' : 'border-line bg-white text-ink' : 'border-transparent bg-white text-ink-soft')} data-id="element-499" data-mp="12oga3wakuklt">{option.label}</button>)}</div></div>

            <div className="mt-5" data-id="element-500" data-mp="1hcq3dljbkcp0"><Field label="Notas" data-id="element-501" data-mp="12lqxwjds649n"><Textarea placeholder="Contexto, ejecución y aprendizajes de esta operación…" value={notes} onChange={e => setNotes(e.target.value)} data-id="element-502" data-mp="12l7dmw0nr3sg" /></Field></div>
            <div className="mt-5" data-id="element-503" data-mp="17ejzwzhv6yi1"><span className="mb-1.5 block text-[13px] font-medium text-ink-soft" data-id="element-504" data-mp="tlax5hof9b8h">Capturas de la operación</span><ImageUploader images={images} onChange={setImages} data-id="element-505" data-mp="yjvyjkfeu0yi" /></div>
          </div>

          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3.5" data-id="element-506" data-mp="yhro0jn0a3p9">
            {showErrors && missing.length > 0 && <p className="mr-auto text-[12px] font-medium text-neg" data-id="element-507" data-mp="olg7tikaotpj">Falta completar: {missing.join(' · ')}</p>}
            <Button type="button" variant="ghost" onClick={onCancel} data-id="element-508" data-mp="141mvxtvj2pf8">Cancelar</Button>
            <Button type="submit" variant="primary" data-id="element-509" data-mp="1035kiy52qss0">{initial ? 'Guardar cambios' : 'Guardar operación'}</Button>
          </footer>
        </motion.form>
      </motion.div>
    </AnimatePresence>;
}